import React from "react";
import { Preferences } from "@capacitor/preferences";
import { formatDate } from "date-fns";
import {
  getDailyStatus,
  recordActivityAdherence,
} from "../../services/dashboard";
import RecordActivity from "../ui/popups/RecordActivity";
import { ActivityConfig, ActivityUpdateData } from "../../types/homeTypes";

import { useStorage } from "../../contexts/StorageContext";
import { ionicStorage } from "../../services/storageService";

// Only initialize storage once and ensure .create() is called before use

/**
 * Activity Manager class for handling activity operations
 * Contains both static methods and a React component
 */
class ActivityManager {
  /**
   * Activity type configuration mapping
   * Defines UI and behavior parameters for different activity types
   */
  static ACTIVITY_CONFIG: Record<string, ActivityConfig> = {
    breathing_treatment: {
      title: "Breathing Routines",
      activityLabel: "Completed Breathing Routines",
      maxValue: 5,
      accentColor: "#00A9CE",
      lightAccentColor: "",
    },
    vest_treatment: {
      title: "Vest Treatments",
      activityLabel: "Completed Vest Treatments",
      maxValue: 5,
      accentColor: "#00A9CE",
      lightAccentColor: "",
    },
    wellbeing: {
      title: "Well-being",
      activityLabel: "well-being",
      maxValue: 5,
      accentColor: "#00b8c9",
      lightAccentColor: "#87e0ea",
    },
    // Add more activity types as needed
  };

  /**
   * Fetches user's daily activity data from the API
   * Returns the results
   */
  static async fetchUserActivityData(
    profileId: number | null | undefined,
    date: string
  ) {
    try {
      console.log(
        "Fetching user activity data for profileId:",
        profileId,
        "on date:",
        date
      );
      // Only proceed if profileId is valid
      const { storage, isStorageReady } = useStorage();
      if (!profileId) {
        console.log("No profile ID provided, skipping activity data fetch");
        return null;
        // throw new Error("No profile ID provided");
      }

      const response = await getDailyStatus(profileId, date);
      console.log("User activity data:", response);

      if (storage && isStorageReady) {
        await ionicStorage(storage, "dailyStatus", JSON.stringify(response));
      }

      // Store in local storage
      await Preferences.set({
        key: "dailyStatus",
        value: JSON.stringify(response),
      });

      return response;
    } catch (error) {
      console.log("Error fetching user activity data:", error);
      throw error;
    }
  }

  /**
   * Records activity adherence to API
   */
  static async recordActivityAdherence(data: {
    profile_id: number;
    adherence_type: string;
    activity_value: number;
    date: string;
    activity_duration: null;
    notes: string;
    time_zone?: string;
  }) {
    return await recordActivityAdherence(data);
  }

  /**
   * React component for the activity recording modal
   */
  static RecordActivityModal: React.FC<{
    activeModal: string | null;
    onClose: () => void;
    getCurrentActivityValue: (activityType: string) => number;
    handleActivityUpdate: (data: ActivityUpdateData) => Promise<void>;
  }> = ({
    activeModal,
    onClose,
    getCurrentActivityValue,
    handleActivityUpdate,
  }) => {
    if (!activeModal) return null;

    const currentActivityConfig = this.ACTIVITY_CONFIG[activeModal];
    if (!currentActivityConfig) return null;

    return (
      <RecordActivity
        isOpen={!!activeModal}
        onClose={onClose}
        onSave={(data: any) =>
          handleActivityUpdate({
            activityType: activeModal,
            value: data.value,
            date: data.date,
            successMessage: `${currentActivityConfig.title} updated successfully`,
            closeModal: onClose,
          })
        }
        title={currentActivityConfig.title}
        activityLabel={currentActivityConfig.activityLabel}
        initialValue={getCurrentActivityValue(activeModal)}
        maxValue={currentActivityConfig.maxValue}
        initialDate={new Date()}
        accentColor={currentActivityConfig.accentColor}
        lightAccentColor={currentActivityConfig.lightAccentColor}
      />
    );
  };
}

export default ActivityManager;
